import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Users, Home, CheckSquare, ArrowRight, Crown, Shield } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { fmtStamp } from "../lib/constants";

const ROLE_LINE = {
  master: { icon: Crown, text: "You hold the master key to this workspace — every setting, every team member, every deal is yours to shape." },
  admin: { icon: Shield, text: "You've been given full administrator access — the team is counting on your leadership." },
};

export default function WelcomeModal() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || !user.first_login_pending) return null;

  const first = (user.name || "there").split(" ")[0];
  const role = ROLE_LINE[user.role];
  const dismiss = async () => {
    setBusy(true);
    try { await api.post("/auth/welcome-seen"); } catch {}
    setUser({ ...user, first_login_pending: false });
  };

  return (
    <Dialog open onOpenChange={(v) => !v && dismiss()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-white border-0" data-testid="welcome-modal"
        onInteractOutside={(e) => e.preventDefault()} onFocusOutside={(e) => e.preventDefault()}>
        <div className="bg-navy-900 px-8 pt-10 pb-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gold/20 blur-3xl" />
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 14 }}
            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center shadow-lg mb-5">
            <Sparkles className="w-7 h-7 text-navy-900" />
          </motion.div>
          <motion.h2 initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
            className="font-display text-3xl font-extrabold text-white leading-tight" data-testid="welcome-title">
            Welcome aboard, <span className="text-gold">{first}</span>!
          </motion.h2>
          <motion.p initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="text-slate-300 mt-3 leading-relaxed">
            We're genuinely thrilled you're here. This is your Global Advancement command center — built so that every lead, conversation, and closing feels effortless. Today is a great day to start something big.
          </motion.p>
          {role && (
            <motion.p initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="flex items-start gap-2 text-sm text-gold/90 mt-4">
              <role.icon className="w-4 h-4 mt-0.5 shrink-0" />{role.text}
            </motion.p>
          )}
        </div>
        <div className="px-8 py-6">
          <p className="text-xs font-bold uppercase tracking-wider text-gold-dark mb-3">A few great first moves</p>
          <ul className="space-y-2.5 text-sm text-slate-600">
            {[[Users, "Open Contacts and meet the people already in your database."], [Home, "Browse Properties — every listing has a map, photos, and its owner one click away."], [CheckSquare, "Check Tasks & Follow-Ups so nothing slips through the cracks."]].map(([I, t], i) => (
              <motion.li key={i} initial={{ x: -8, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.35 + i * 0.08 }} className="flex items-start gap-2.5">
                <I className="w-4 h-4 text-gold-dark mt-0.5 shrink-0" />{t}
              </motion.li>
            ))}
          </ul>
          <p className="text-[11px] text-slate-400 mt-5">Signed in as {user.email} · {fmtStamp(new Date().toISOString())}. Every change you make is stamped with your name so the whole team stays in sync.</p>
          <button onClick={dismiss} disabled={busy} data-testid="welcome-continue-btn"
            className="mt-5 w-full bg-navy-800 hover:bg-navy-700 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            Let's get started <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
